<template>
  <div class="m-meter">
    <div class="m-meter__top">
      <span class="m-meter__label">{{ label }}</span>
      <span class="m-meter__value" :class="`is-${tone}`">{{ displayValue }}</span>
    </div>
    <div v-if="quota !== null" class="m-meter__bar">
      <span :class="`is-${tone}`" :style="{ width: pct + '%' }"></span>
    </div>
    <div v-else class="m-meter__unlimited">no cap this period</div>
  </div>
</template>

<script>
/**
 * MusubiUsageMeter — a compact usage bar for one meter (used/quota). Handles
 * quota === null as UNLIMITED: no bar, no percentage — just the live used
 * count, per the mockup's "Unlimited — no meters enforced" treatment for the
 * internal plan (every meter component must honour this, not just the page
 * shell around it).
 *
 *   <MusubiUsageMeter label="Emails" :used="240" :quota="5000" />
 *   <MusubiUsageMeter label="Prospects" :used="1050" :quota="null" />
 */
export default {
  name: 'MusubiUsageMeter',
  props: {
    label: { type: String, required: true },
    used:  { type: Number, required: true },
    quota: { type: Number, default: null },
  },
  computed: {
    pct() {
      if (this.quota === null || this.quota <= 0) return 0;
      return Math.min(100, Math.round((this.used / this.quota) * 100));
    },
    tone() {
      if (this.quota === null) return 'unlimited';
      if (this.pct >= 100) return 'over';
      if (this.pct >= 80) return 'warn';
      return 'ok';
    },
    displayValue() {
      if (this.quota === null) return this.used.toLocaleString();
      return `${this.pct}%`;
    },
  },
};
</script>

<style scoped>
.m-meter__top { display: flex; justify-content: space-between; align-items: baseline; gap: 6px; }
.m-meter__label { font-size: 0.6rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--m-text-tertiary); }
.m-meter__value { font-size: 0.74rem; font-weight: 700; font-family: var(--m-font-mono); color: var(--m-text-secondary); }
.m-meter__value.is-warn { color: var(--m-status-warning); }
.m-meter__value.is-over { color: var(--m-status-danger); }
.m-meter__value.is-unlimited { color: var(--m-text-tertiary); }
.m-meter__bar { height: 6px; border-radius: 999px; background: var(--m-border); overflow: hidden; margin-top: 4px; }
.m-meter__bar span { display: block; height: 100%; border-radius: 999px; background: var(--m-status-success); }
.m-meter__bar span.is-warn { background: var(--m-status-warning); }
.m-meter__bar span.is-over { background: var(--m-status-danger); }
.m-meter__unlimited { font-size: 0.68rem; color: var(--m-text-tertiary); margin-top: 4px; }
</style>
